<!--
  A view that lays out all claim items as a summary of current items in claim
-->
<template>
  <div>
    <div class="claims_items_summary_forn">
      <!-- Subtitle -->
      <div class="subtitle">{{ $t(`${pagei18nKey}.title`) }}</div>

      <div class="claims_items_summary_list">
        <!-- Baggage list items -->
        <claims-item
          v-for="(item, index) in baggageItems"
          class="claims_items_summary_list_item"
          :description="getBaggageItemDescription(item)"
          :displaySubtitle="imagesEnabled"
          :numberOfImages="getNumberOfImages(item)"
          :numberOfReceipts="getNumberOfReceipts(item)"
          :key="item.id"
          @deleteClicked="openBaggageDeleteConfirmation(index)"
          @editClicked="editBaggageItem(index)" />

        <!-- Contents list item -->
        <claims-item
          v-for="(item, index) in contentItems"
          class="claims_items_summary_list_item"
          :description="getContentsItemDescription(item)"
          :displaySubtitle="imagesEnabled"
          :numberOfImages="getNumberOfImages(item)"
          :numberOfReceipts="getNumberOfReceipts(item)"
          :key="item.id"
          @deleteClicked="openContentsDeleteConfirmation(index)"
          @editClicked="editContentsItem(index)" />
      </div>

      <!-- Add another item button -->
      <div class="claims_items_summary_button_container">
        <app-button
          @click.native="onAddItemClick"
          class="claims_items_summary_button"
          :state="buttonState"
          :type="buttonType"
          :text="$t(`${pagei18nKey}.addButton`)"/>

        <!-- baggage delete confirmation -->
        <ms-app-message
          v-if="showBaggageDeleteConfirmation"
          :title="$t(`${pagei18nKey}.deleteItemTitle`)"
          :isOpen.sync="showBaggageDeleteConfirmation"
          :buttons="[ noButton, yesButton ]"
          @buttonClick="baggageAppMessageButtonClick">
          {{ $t(`${this.pagei18nKey}.deleteConfirmationMessage`) }} {{ `${getBaggageDescFromIndex(baggageItemIndexToDelete)}?` }}
        </ms-app-message>

        <!-- Contents delete confirmation -->
        <ms-app-message
          v-if="showContentsDeleteConfirmation"
          :title="$t(`${pagei18nKey}.deleteItemTitle`)"
          :isOpen.sync="showContentsDeleteConfirmation"
          :buttons="[ noButton, yesButton ]"
          @buttonClick="contentsAppMessageButtonClick">
          {{ $t(`${this.pagei18nKey}.deleteConfirmationMessage`) }} {{ `${getContentsDescFromIndex(contentsItemIndexToDelete)}?` }}
        </ms-app-message>
      </div>
    </div>

    <!-- Navigation buttons -->
    <div class="claims_items_summary_button__container">
      <ms-navigation-bar
        :buttons="[backButton, nextButton]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>
  </div>
</template>

<script>
  import ClaimsItem from '@/components/claims-item'
  import AppButton, {ButtonStates, ButtonTypes} from '@/components/app-button'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import MsAppMessage, {defaultModalButtons} from '@/components/ms-app-message'
  import i18n from '@/lang'
  import themeSettings from '@/theme-settings.json'

  const arrayNames = {
    BAGGAGE: 'baggageItems',
    CONTENTS: 'contentItems'
  }

  export default {
    components: {
      ClaimsItem,
      AppButton,
      MsNavigationBar,
      MsAppMessage
    },

    data () {
      return {
        pagei18nKey: 'message.claimsItemsSummary',
        showBaggageDeleteConfirmation: false,
        showContentsDeleteConfirmation: false,
        baggageItemIndexToDelete: null,
        contentsItemIndexToDelete: null,
        imagesEnabled: !!themeSettings.theme.imagesEnabled
      }
    },

    methods: {
      /**
       * Get number of images in an item object
       *
       * @param {Object} item - Item to get number of images
       * @returns {number} Number of images in item
       */
      getNumberOfImages: function (item) {
        var number = 0

        // Images
        for (var key in item.images) {
          if (item.images[key] && item.images[key].imageUrl) number++
        }

        // AreaOfDamage images
        for (var image in item.areaOfDamageImages) {
          if (item.areaOfDamageImages[image].imageUrl) number++
        }

        return number
      },

      /**
       * Get number of receipts in an item object
       *
       * @param {Object} item - Item to get number of receipts
       * @returns {number} Number of receipts in item
       */
      getNumberOfReceipts: function (item) {
        return (item.receipt.imageUrl !== null ? 1 : 0)
      },

      /**
       * Open the confirm delete app message
       *
       * @param {string} index - The index of item to delete
       */
      openBaggageDeleteConfirmation: function (index) {
        this.showBaggageDeleteConfirmation = true
        this.baggageItemIndexToDelete = index
      },

      /**
       * Open the confirm delete app message
       *
       * @param {string} index - The index of item to delete
       */
      openContentsDeleteConfirmation: function (index) {
        this.showContentsDeleteConfirmation = true
        this.contentsItemIndexToDelete = index
      },

      /**
       * On button click in delete confirmation
       *
       * @param {Object} button - Clicked button object
       */
      baggageAppMessageButtonClick: function (button) {
        // If the yes button has been clicked then delete relevant item
        if (button.id === defaultModalButtons.YES.id) {
          this.deleteBaggageItem(this.baggageItemIndexToDelete)
        } else {
          this.baggageItemIndexToDelete = null
        }
        this.showBaggageDeleteConfirmation = false
      },

      /**
       * On button click in delete confirmation
       *
       * @param {Object} button - Clicked button object
       */
      contentsAppMessageButtonClick: function (button) {
        // If the yes button has been clicked then delete relevant item
        if (button.id === defaultModalButtons.YES.id) {
          this.deleteContentsItem(this.contentsItemIndexToDelete)
        } else {
          this.contentsItemIndexToDelete = null
        }
        this.showContentsDeleteConfirmation = false
      },

      /**
       * Delete baggage item
       *
       * @param {number} index - index of baggage item to delete
       */
      deleteBaggageItem: function (index) {
        this.$store.commit('deleteBaggageItem', index)
      },

      /**
       * Delete contents item
       *
       * @param {number} index - index of contents item to delete
       */
      deleteContentsItem: function (index) {
        this.$store.commit('deleteContentsItem', index)
      },

      /**
       * Send back to add claims item with array and index params
       * of item to edit
       *
       * @param {number} index - Index of baggage item
       */
      editBaggageItem: function (index) {
        this.$router.replace(`/add-claims-item/${arrayNames.BAGGAGE}/${index}`)
      },

      /**
       * Send back to add claims item with array and index params
       * of item to edit
       *
       * @param {number} Index - Index of contents item
       */
      editContentsItem: function (index) {
        this.$router.replace(`/add-claims-item/${arrayNames.CONTENTS}/${index}`)
      },

      /**
       * Get list item baggage description
       *
       * @param {Object} item - Object of baggage item to get description for
       * @returns {string} Description to show
       */
      getBaggageItemDescription: function (item) {
        return `${item.additionalBrandInfo || item.brandString}, ${item.colourString}, ${item.additionalMaterialInfo || item.materialString}`
      },

      /**
       * Get list item contents description
       *
       * @param {Object} item - Object of contents item to get description for
       * @returns {string} Description to show
       */
      getContentsItemDescription: function (item) {
        return `${item.name}, ${item.damageDesc}`
      },

      /**
       * Go to add damaged item page to add new item
       */
      onAddItemClick: function () {
        this.$router.replace('/add-claims-item')
      },

      /**
       * Get baggage item description from item's index
       *
       * @param {number} index - index of claim item
       * @returns {string} Description of item
       */
      getBaggageDescFromIndex: function (index) {
        return this.getBaggageItemDescription(this.baggageItems[index])
      },

      /**
       * Get contents item description from item's index
       *
       * @param {number} index - index of claim item
       * @returns {string} Description of item
       */
      getContentsDescFromIndex: function (index) {
        return this.getContentsItemDescription(this.contentItems[index])
      },

      /**
       * Change route
       *
       * @param {string} buttonId - Id of button
       */
      navigationButtonClick (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.$router.go(-1)
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          this.$router.push('/submit-claim')
        }
      }
    },

    computed: {
      /**
       * Get current stored baggage items array
       */
      baggageItems: {
        get () {
          return this.$store.getters.baggageItems
        }
      },

      /**
       * Get current stored contents items array
       */
      contentItems: {
        get () {
          return this.$store.getters.contentItems
        }
      },

      /**
       * Get the concatenated items from both arrays
       */
      items: {
        get () {
          return this.baggageItems.concat(this.contentItems)
        }
      },

      /**
       * Get button type
       */
      buttonType: {
        get () {
          return ButtonTypes.TERTIARY
        }
      },

      /**
       * Get button state
       */
      buttonState: {
        get () {
          return ButtonStates.ENABLED
        }
      },

      noButton: {
        get () {
          return Object.assign(
            defaultModalButtons.NO,
            {
              'text': i18n.t('message.cancelButton')
            }
          )
        }
      },

      yesButton: {
        get () {
          return Object.assign(
            defaultModalButtons.YES,
            {
              'text': i18n.t('message.deleteButton')
            }
          )
        }
      },

      /**
       * Get back button object
       */
      backButton: {
        get () {
          return DefaultNavigationButtons.BACK
        }
      },

      /**
       * Get next button object
       */
      nextButton: {
        get () {
          // Clone DefaultNavigationButtons.NEXT and remove reference to it
          let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
          return Object.assign(
            defaultNextButton,
            {
              state: ButtonStates.ENABLED
            }
          )
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../assets/sass/main.scss";

  .claims_items_summary_forn {
    background-color: $quaternary-brand-color;
    @extend %app_form_container;
  }

  .claims_items_summary_list_item {
    padding: $dims-spacing-large $dims-horizontal-gutter;
    border-top: $default-border-style;

    &:last-child {
      border-bottom: $default-border-style;
    }
  }

  .claims_items_summary_button__container {
    @include mobile {
      padding: 0 $dims-horizontal-gutter;
    }
  }

  .claims_items_summary_button {
    max-width: none;
    margin: $dims-spacing-xlarge auto;
    padding: $dims-spacing-small 0;
  }
</style>
